// EVERY EXPENSE THE BUSINESS HAS, in one screen.
//
// THE MIRROR OF THE MONEY SCREEN. That one answers "who owes us"; this one answers "what
// do we still owe". Until it existed the second question had no screen at all: an unpaid
// cost inside a job was visible only to somebody who opened that job, so a journalist
// invoiced in July and not yet paid was, in practice, forgotten.
//
// ONE TABLE, TWO SECTIONS. Office overhead and a cost on a job are the same rows in the
// same table, told apart by `job_id` alone (migration 026) — they are all business
// expenses, and the split is how they are read, not what they are. The server returns
// both halves of one query already divided; nothing here decides which is which.
//
// ⚠ THE RED DOT READS THE SERVER'S TALLY AND NEVER THE ROWS ON SCREEN. The screen shows
// one state at a time, so a count taken from `rows` reads zero the moment somebody opens
// the archive — the indicator would go dark exactly while the money is still owed, which
// is the single thing it exists to prevent. `data.unpaid` is counted over `is_paid = 0`
// on every request and says the same thing in both views.
//
// DISPLAY ONLY, AND NO NEW WRITE. Settling a cost is `MarkPaidModal` → `POST
// /expenses/:id/pay`, the same act the job panel has always offered; recording one is the
// same `ExpenseModal`. No figure moves because a list of expenses is on screen: profit
// counts every expense and the partner gap counts the paid ones, both untouched.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { useReloadAlerts } from '../../context/AlertsContext';
import { expensesApi } from '../../api';
import he from '../../i18n/he';
import { formatMoney } from '../../components/Money';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import ExpenseModal from '../../components/ExpenseModal';
import MarkPaidModal from '../../components/MarkPaidModal';
import ExpenseSection from './ExpenseSection';

export default function Expenses() {
  const lookups = useLookups();
  // WHICH HALF IS ON SCREEN — and it is a FETCH DEP, not a filter over one payload. The
  // clients and retainers screens split their one list in the browser because both halves
  // are bounded and the archive is the small one. Expenses invert that: the unpaid list
  // is short by definition and the paid archive grows every month forever, so fetching it
  // to show the handful of open costs gets slower for the rest of the system's life. The
  // two halves are also ordered by different dates, which one payload cannot be.
  const [showPaid, setShowPaid] = useState(false);
  const { data, loading, error, reload } = useAsync(
    () => expensesApi.overview(showPaid),
    [showPaid],
  );
  const [editing, setEditing] = useState(null); // expense row, {} for new, or null
  const [paying, setPaying] = useState(null); // the unpaid expense being settled
  const reloadAlerts = useReloadAlerts();

  // A deleted cost may have been a charge waiting to be raised — the money dot's amber.
  const remove = async (row) => {
    await expensesApi.remove(row.id);
    reload();
    reloadAlerts();
  };

  // A JOB EXPENSE EDITS AS A JOB EXPENSE. `ExpenseModal` decides whether to ask who bears
  // the cost from whether it was given a job (migration 054 — an office cost has no
  // client, so there is nobody for it to fall on), and the category list is scoped the
  // same way. Passing the row's own `job_id` is what keeps editing from this screen
  // identical to editing from the job it belongs to.
  //
  // Adding from here adds an OFFICE cost. A cost on a job is recorded on that job, where
  // the bearer and the charge are decided alongside it.
  const editingJobId = editing && editing.job_id != null ? editing.job_id : null;

  // Defaulted so the header renders identically while the first fetch is in flight —
  // the line reads "nothing outstanding" for a moment rather than flickering in.
  const unpaid = (data && data.unpaid) || { count: 0, total: 0, toRecover: 0, charged: 0 };

  return (
    <>
      <PageHeader title={he.expenseOverview.title} spread>
        <div className="row">
          <div className="seg">
            <Button small variant={showPaid ? 'subtle' : ''} onClick={() => setShowPaid(false)}>
              {he.expenseOverview.showUnpaid}
            </Button>
            <Button small variant={showPaid ? '' : 'subtle'} onClick={() => setShowPaid(true)}>
              {he.expenseOverview.showPaid}
            </Button>
          </div>
          {/* The dot and its line, in BOTH states — see the header. The same 8px red mark
              the sidebar uses for "something here is late", because it is the same
              vocabulary: red means somebody is waiting. */}
          <span>
            <span className="row">
              {unpaid.count > 0 && <span className="nav-dot nav-dot-overdue" />}
              <span className={unpaid.count > 0 ? 'strong' : 'muted'}>
                {unpaid.count > 0
                  ? he.expenseOverview.stillOwed(unpaid.count, formatMoney(unpaid.total))
                  : he.expenseOverview.nothingOwed}
              </span>
            </span>
            {/* HOW MUCH OF THAT COMES BACK — beneath the headline, never netted into it.
                The figure above is an OBLIGATION: we owe the supplier the whole of it
                whoever ends up bearing the cost, so it is right as it stands. What it
                cannot say alone is that part of it returns, and read as a spend it
                overstates what the business costs by exactly the client-borne part.
                These two say the rest, and the subtraction is left visible rather than
                done silently — netting them into one number would answer the second
                question by destroying the first.

                Shown only when there is something to say: a screen where nothing is
                charged on carries no line at all. */}
            {(unpaid.toRecover > 0 || unpaid.charged > 0) && (
              <div className="muted small-note">
                {unpaid.toRecover > 0
                  && he.expenseOverview.ofWhichToRecoverLine(formatMoney(unpaid.toRecover))}
                {unpaid.toRecover > 0 && unpaid.charged > 0 && ' · '}
                {unpaid.charged > 0
                  && he.expenseOverview.alreadyChargedLine(formatMoney(unpaid.charged))}
              </div>
            )}
          </span>
        </div>
        <Button onClick={() => setEditing({})}>{he.officeExpenses.add}</Button>
      </PageHeader>

      <AsyncBoundary loading={loading} error={error}>
        {data && (
          <>
            <ExpenseSection
              section="office"
              title={he.expenseOverview.officeSection}
              rows={data.office}
              empty={data.paid ? he.expenseOverview.emptyPaidOffice : he.expenseOverview.emptyUnpaidOffice}
              paid={data.paid}
              lookups={lookups}
              onMarkPaid={setPaying}
              onEdit={setEditing}
              onDelete={remove}
            />
            <ExpenseSection
              section="jobs"
              title={he.expenseOverview.jobsSection}
              rows={data.jobs}
              empty={data.paid ? he.expenseOverview.emptyPaidJobs : he.expenseOverview.emptyUnpaidJobs}
              paid={data.paid}
              lookups={lookups}
              onMarkPaid={setPaying}
              onEdit={setEditing}
              onDelete={remove}
            />
          </>
        )}
      </AsyncBoundary>

      {editing && (
        <ExpenseModal
          jobId={editingJobId}
          context={editingJobId == null ? 'office' : 'job'}
          expense={editing}
          owner={{ title: editing.job_title, clientName: editing.client_name }}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {paying && (
        <MarkPaidModal
          expense={paying}
          onClose={() => setPaying(null)}
          onSaved={() => { setPaying(null); reload(); }}
        />
      )}
    </>
  );
}
